import type { Deal } from "@/components/atomic-crm/types";

interface NaiuStageTaskTemplate {
  autoKey: string;
  type: string;
  text: string;
  dueOffsetDays: number;
}

function todayIsoDate(now = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const NAIU_PRICED_STAGES = new Set([
  "cart",
  "proposal",
  "payment",
  "won",
  "active",
  "repurchase",
  "lost",
]);

const NAIU_PURCHASE_STAGES = new Set([
  "payment",
  "won",
  "active",
  "repurchase",
]);

const NAIU_DELIVERY_STAGES = new Set(["won", "active", "repurchase"]);

const NAIU_EXPECTED_CLOSING_STAGES = new Set([
  "cart",
  "proposal",
  "payment",
]);

export function naiuStageHasPrice(stage: string): boolean {
  return NAIU_PRICED_STAGES.has(stage);
}

export function naiuStageHasDiagnosisDate(_stage: string): boolean {
  return false;
}

export function naiuStageHasPurchaseDate(stage: string): boolean {
  return NAIU_PURCHASE_STAGES.has(stage);
}

export function naiuStageHasDeliveryDate(stage: string): boolean {
  return NAIU_DELIVERY_STAGES.has(stage);
}

export function naiuStageHasExpectedClosing(stage: string): boolean {
  return NAIU_EXPECTED_CLOSING_STAGES.has(stage);
}

export function getNaiuStageTaskTemplates(
  stage: string,
  deal: Deal,
): NaiuStageTaskTemplate[] {
  const name = deal.name;

  if (stage === "captured") {
    return [
      {
        autoKey: "captured:first-contact",
        type: "whatsapp",
        text: `Primer contacto con ${name}. Anotar de dónde llegó y qué busca.`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "conversation") {
    return [
      {
        autoKey: "conversation:product",
        type: "whatsapp",
        text: `Resolver la duda de producto con ${name} (línea, dosis, Vital o Fungi).`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "cart") {
    return [
      {
        autoKey: "cart:confirm",
        type: "whatsapp",
        text: `Confirmar el pedido y la forma de pago de ${name}.`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "proposal") {
    return [
      {
        autoKey: "proposal:send",
        type: "follow-up",
        text: `Enviar el precio del pedido de empresa a ${name}.`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "payment") {
    return [
      {
        autoKey: "payment:receipt",
        type: "follow-up",
        text: `Verificar el comprobante de ${name} antes de despachar.`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "won") {
    return [
      {
        autoKey: "won:dispatch",
        type: "follow-up",
        text: `Marcar el despacho de ${name} y guardar la fecha de compra.`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "active") {
    return [
      {
        autoKey: "active:follow",
        type: "follow-up",
        text: `Hacer seguimiento a ${name}.`,
        dueOffsetDays: 0,
      },
      {
        autoKey: "active:repurchase-date",
        type: "follow-up",
        text: `Escribir la fecha de recompra de ${name}. Un mes, o los días que dure el producto.`,
        dueOffsetDays: 0,
      },
    ];
  }
  if (stage === "repurchase") {
    return [
      {
        autoKey: "repurchase:ping",
        type: "whatsapp",
        text: `Escribir a ${name}: se está acabando el producto.`,
        dueOffsetDays: 0,
      },
    ];
  }
  return [];
}

export function buildNaiuStageDatePatch(
  deal: Deal,
  nextStage: string,
  now = new Date(),
): Partial<Deal> {
  const patch: Partial<Deal> = {};
  if (naiuStageHasPurchaseDate(nextStage) && !deal.purchase_date) {
    patch.purchase_date = todayIsoDate(now);
  }
  if (nextStage === "won" && !deal.delivery_date) {
    patch.delivery_date = todayIsoDate(now);
  }
  return patch;
}
